/**
 * Accountant Tax Workspace — Per-client tax summary with earnings breakdown,
 * PAYE deductions, SED status, and tax liability calculation.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  ArrowLeft, Calculator, PoundSterling, Ship, ShieldCheck, Loader2,
  RefreshCw, FileText, TrendingUp, TrendingDown, AlertTriangle, CheckCircle2,
  Edit2, Save, X
} from 'lucide-react';
import { Input } from '../../components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const money = (v) => `£${Math.abs(parseFloat(v) || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}`;

const AccountantTaxWorkspace = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState(null);
  const [taxYear, setTaxYear] = useState('');
  const [taxYears, setTaxYears] = useState([]);
  const [taxIds, setTaxIds] = useState({ ni_number: '', utr: '', date_of_birth: '' });
  const [editingIds, setEditingIds] = useState(false);
  const [editTaxIds, setEditTaxIds] = useState({ ni_number: '', utr: '', date_of_birth: '' });
  const [savingIds, setSavingIds] = useState(false);

  const fetchTaxYears = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/accountant/clients/${clientId}/trips`);
      const trips = res.data.trips || [];

      const yearSet = new Set();
      trips.forEach(t => {
        const dateStr = t.date_left_uk || t.date_joined_vessel;
        if (!dateStr) return;
        const d = new Date(dateStr);
        const m = d.getMonth() + 1;
        const y = d.getFullYear();
        const ty = m >= 4 ? `${y}/${y + 1}` : `${y - 1}/${y}`;
        yearSet.add(ty);
      });

      const now = new Date();
      const cm = now.getMonth() + 1;
      const cy = now.getFullYear();
      const currentTy = cm >= 4 ? `${cy}/${cy + 1}` : `${cy - 1}/${cy}`;
      yearSet.add(currentTy);

      const allYears = [...yearSet].sort().reverse();
      setTaxYears(allYears);
      setTaxYear(prev => prev || allYears[0]);
    } catch {
      const now = new Date();
      const m = now.getMonth() + 1;
      const y = now.getFullYear();
      const currentTy = m >= 4 ? `${y}/${y+1}` : `${y-1}/${y}`;
      const fallback = [];
      const start = parseInt(currentTy.split('/')[0]);
      for (let yr = start; yr >= start - 5; yr--) {
        fallback.push(`${yr}/${yr + 1}`);
      }
      setTaxYears(fallback);
      setTaxYear(prev => prev || fallback[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const fetchSummary = useCallback(async (ty) => {
    if (!ty) return;
    try {
      const res = await axios.get(`${API_URL}/api/accountant/clients/${clientId}/tax-summary?tax_year=${ty}`);
      setData(res.data);
    } catch {
      toast.error('Failed to load tax summary');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  useEffect(() => { fetchTaxYears(); }, [fetchTaxYears]);
  useEffect(() => { if (taxYear) { setLoading(true); fetchSummary(taxYear); } }, [taxYear, fetchSummary]);

  // Fetch tax identifiers
  useEffect(() => {
    axios.get(`${API_URL}/api/accountant/clients/${clientId}/tax-identifiers`).then(res => {
      const ids = { ni_number: res.data.ni_number || '', utr: res.data.utr || '', date_of_birth: res.data.date_of_birth || '' };
      setTaxIds(ids);
      setEditTaxIds(ids);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const handleSaveTaxIds = async () => {
    setSavingIds(true);
    try {
      await axios.put(`${API_URL}/api/accountant/clients/${clientId}/tax-identifiers`, editTaxIds);
      setTaxIds(editTaxIds);
      setEditingIds(false);
      toast.success('Client details saved');
    } catch {
      toast.error('Failed to save');
    } finally {
      setSavingIds(false);
    }
  };

  const handleRefresh = () => { setRefreshing(true); fetchSummary(taxYear); };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-sky-400" />
      </div>
    );
  }

  const s = data?.summary || {};
  const tc = data?.tax_calculation || {};
  const isRefund = tc.refund_due > 0;

  return (
    <div className="space-y-4" data-testid="tax-workspace">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/accountant/client/${clientId}`)}
            className="text-slate-400 hover:text-white h-8 w-8" data-testid="back-btn">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wider">Tax Workspace</p>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2" data-testid="client-name-header">
              {data?.client_name || 'Client'}
            </h1>
            {/* Tax identifiers row */}
            {!editingIds ? (
              <div className="flex items-center gap-4 mt-1.5 flex-wrap">
                {taxIds.ni_number && (
                  <span className="text-xs text-slate-400" data-testid="display-ni">
                    <span className="text-slate-600">NI:</span> <span className="text-slate-300 font-mono">{taxIds.ni_number}</span>
                  </span>
                )}
                {taxIds.utr && (
                  <span className="text-xs text-slate-400" data-testid="display-utr">
                    <span className="text-slate-600">UTR:</span> <span className="text-slate-300 font-mono">{taxIds.utr}</span>
                  </span>
                )}
                {taxIds.date_of_birth && (
                  <span className="text-xs text-slate-400" data-testid="display-dob">
                    <span className="text-slate-600">DOB:</span> <span className="text-slate-300">{new Date(taxIds.date_of_birth).toLocaleDateString('en-GB')}</span>
                  </span>
                )}
                {!taxIds.ni_number && !taxIds.utr && !taxIds.date_of_birth && (
                  <span className="text-xs text-slate-500">No tax identifiers set</span>
                )}
                <button onClick={() => { setEditTaxIds(taxIds); setEditingIds(true); }}
                  className="text-sky-400 hover:text-sky-300 transition-colors" data-testid="edit-tax-ids-btn">
                  <Edit2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <div className="flex items-end gap-2 mt-2 flex-wrap" onClick={e => e.stopPropagation()}>
                <div>
                  <label className="text-[10px] text-slate-500 uppercase">NI Number</label>
                  <Input value={editTaxIds.ni_number} onChange={e => setEditTaxIds(p => ({...p, ni_number: e.target.value}))}
                    placeholder="AB 12 34 56 C" className="bg-slate-900 border-slate-600 text-white h-7 text-xs w-36 uppercase font-mono"
                    data-testid="edit-ni-input" />
                </div>
                <div>
                  <label className="text-[10px] text-slate-500 uppercase">UTR</label>
                  <Input value={editTaxIds.utr} onChange={e => setEditTaxIds(p => ({...p, utr: e.target.value}))}
                    placeholder="1234567890" className="bg-slate-900 border-slate-600 text-white h-7 text-xs w-32 font-mono"
                    data-testid="edit-utr-input" />
                </div>
                <div>
                  <label className="text-[10px] text-slate-500 uppercase">DOB</label>
                  <Input type="date" value={editTaxIds.date_of_birth} onChange={e => setEditTaxIds(p => ({...p, date_of_birth: e.target.value}))}
                    className="bg-slate-900 border-slate-600 text-white h-7 text-xs w-36"
                    data-testid="edit-dob-input" />
                </div>
                <Button size="sm" onClick={handleSaveTaxIds} disabled={savingIds}
                  className="bg-sky-600 hover:bg-sky-700 h-7 px-2" data-testid="save-tax-ids-btn">
                  {savingIds ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setEditingIds(false)}
                  className="text-slate-400 hover:text-white h-7 px-2">
                  <X className="h-3 w-3" />
                </Button>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select value={taxYear} onChange={e => setTaxYear(e.target.value)}
            className="bg-slate-800 border border-slate-600 text-white rounded-md h-9 text-sm px-3"
            data-testid="tax-year-select">
            {taxYears.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <Button size="sm" variant="outline" onClick={handleRefresh} disabled={refreshing}
            className="border-slate-600 text-slate-400 hover:text-white h-9" data-testid="refresh-btn">
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button size="sm" onClick={() => navigate(`/accountant/tax-return/${clientId}`)}
            className="bg-amber-600 hover:bg-amber-700 h-9" data-testid="open-sa100-btn">
            <FileText className="h-3.5 w-3.5 mr-1.5" />SA100
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-4">
            <p className="text-[10px] text-slate-500 uppercase mb-1">Gross Earnings</p>
            <p className="text-2xl font-bold text-white">{money(s.total_gross_earnings)}</p>
            <p className="text-[10px] text-slate-500 mt-1">{s.trips_count} trips</p>
          </CardContent>
        </Card>
        <Card className={`border ${s.sed_eligible ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-slate-800 border-slate-700'}`}>
          <CardContent className="p-4">
            <p className="text-[10px] text-slate-500 uppercase mb-1">SED Status</p>
            <div className="flex items-center gap-2">
              {s.sed_eligible ? (
                <><CheckCircle2 className="h-5 w-5 text-emerald-400" /><span className="text-lg font-bold text-emerald-400">Eligible</span></>
              ) : (
                <><AlertTriangle className="h-5 w-5 text-amber-400" /><span className="text-lg font-bold text-amber-400">Not Eligible</span></>
              )}
            </div>
            <p className="text-[10px] text-slate-500 mt-1">{s.qualifying_days} qualifying days</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-4">
            <p className="text-[10px] text-slate-500 uppercase mb-1">PAYE Tax Deducted</p>
            <p className="text-2xl font-bold text-red-400">{money(s.total_income_tax_deducted)}</p>
            <p className="text-[10px] text-slate-500 mt-1">NI: {money(s.total_ni_deducted)}</p>
          </CardContent>
        </Card>
        <Card className={`border ${isRefund ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-red-500/5 border-red-500/20'}`}>
          <CardContent className="p-4">
            <p className="text-[10px] text-slate-500 uppercase mb-1">{isRefund ? 'Refund Due' : 'Tax Owed'}</p>
            <div className="flex items-center gap-2">
              {isRefund ? <TrendingUp className="h-5 w-5 text-emerald-400" /> : <TrendingDown className="h-5 w-5 text-red-400" />}
              <span className={`text-2xl font-bold ${isRefund ? 'text-emerald-400' : 'text-red-400'}`}>
                {money(tc.refund_due)}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Earnings Split */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-white text-sm flex items-center gap-2">
              <PoundSterling className="h-4 w-4 text-amber-400" />
              Earnings Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 pt-0 space-y-3">
            <div className="flex items-center justify-between p-3 bg-slate-900 rounded-lg">
              <div className="flex items-center gap-2">
                <Badge className="bg-blue-500/20 text-blue-400 text-[10px]">PAYE</Badge>
                <span className="text-sm text-slate-300">Salaried / Employer Deducted</span>
              </div>
              <span className="text-sm font-semibold text-white">{money(s.paye_earnings)}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-900 rounded-lg">
              <div className="flex items-center gap-2">
                <Badge className="bg-amber-500/20 text-amber-400 text-[10px]">GROSS</Badge>
                <span className="text-sm text-slate-300">Day Rate / No Tax at Source</span>
              </div>
              <span className="text-sm font-semibold text-white">{money(s.gross_untaxed_earnings)}</span>
            </div>
            {s.sed_eligible && s.sed_deduction > 0 && (
              <div className="flex items-center justify-between p-3 bg-emerald-500/5 border border-emerald-500/20 rounded-lg">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-emerald-400" />
                  <span className="text-sm text-emerald-300">SED Deduction</span>
                </div>
                <span className="text-sm font-semibold text-emerald-400">-{money(s.sed_deduction)}</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* PAYE Deductions Summary */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-white text-sm flex items-center gap-2">
              <Calculator className="h-4 w-4 text-red-400" />
              Deductions Already Paid
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 pt-0 space-y-3">
            <div className="flex items-center justify-between p-3 bg-slate-900 rounded-lg">
              <span className="text-sm text-slate-300">Income Tax (PAYE)</span>
              <span className="text-sm font-semibold text-red-400">{money(s.total_income_tax_deducted)}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-900 rounded-lg">
              <span className="text-sm text-slate-300">National Insurance</span>
              <span className="text-sm font-semibold text-red-400">{money(s.total_ni_deducted)}</span>
            </div>
            {s.total_pension_deducted > 0 && (
              <div className="flex items-center justify-between p-3 bg-slate-900 rounded-lg">
                <span className="text-sm text-slate-300">Pension Contributions</span>
                <span className="text-sm font-semibold text-orange-400">{money(s.total_pension_deducted)}</span>
              </div>
            )}
            {s.total_student_loan_deducted > 0 && (
              <div className="flex items-center justify-between p-3 bg-slate-900 rounded-lg">
                <span className="text-sm text-slate-300">Student Loan</span>
                <span className="text-sm font-semibold text-purple-400">{money(s.total_student_loan_deducted)}</span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Tax Calculation Breakdown */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader className="py-3 px-4">
          <CardTitle className="text-white text-sm flex items-center gap-2">
            <Calculator className="h-4 w-4 text-sky-400" />
            Tax Calculation — {taxYear}
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4 pt-0">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <div className="p-3 bg-slate-900 rounded-lg">
              <p className="text-[10px] text-slate-500 uppercase mb-0.5">Gross Employment</p>
              <p className="text-sm font-semibold text-white">{money(tc.gross_employment)}</p>
            </div>
            <div className="p-3 bg-slate-900 rounded-lg">
              <p className="text-[10px] text-slate-500 uppercase mb-0.5">SED Deduction</p>
              <p className="text-sm font-semibold text-emerald-400">-{money(tc.sed_deduction)}</p>
            </div>
            <div className="p-3 bg-slate-900 rounded-lg">
              <p className="text-[10px] text-slate-500 uppercase mb-0.5">Net Taxable Employment</p>
              <p className="text-sm font-semibold text-white">{money(tc.net_employment)}</p>
            </div>
            <div className="p-3 bg-slate-900 rounded-lg">
              <p className="text-[10px] text-slate-500 uppercase mb-0.5">Personal Allowance</p>
              <p className="text-sm font-semibold text-sky-400">{money(tc.personal_allowance)}</p>
            </div>
            <div className="p-3 bg-slate-900 rounded-lg">
              <p className="text-[10px] text-slate-500 uppercase mb-0.5">Total Income Tax</p>
              <p className="text-sm font-semibold text-red-400">{money(tc.total_income_tax)}</p>
            </div>
            <div className="p-3 bg-slate-900 rounded-lg">
              <p className="text-[10px] text-slate-500 uppercase mb-0.5">Total Tax Liability</p>
              <p className="text-sm font-semibold text-red-400">{money(tc.total_tax_liability)}</p>
            </div>
            <div className="p-3 bg-slate-900 rounded-lg">
              <p className="text-[10px] text-slate-500 uppercase mb-0.5">PAYE Already Paid</p>
              <p className="text-sm font-semibold text-amber-400">{money(tc.paye_tax_paid)}</p>
            </div>
            <div className="p-3 bg-slate-900 rounded-lg">
              <p className="text-[10px] text-slate-500 uppercase mb-0.5">Total Already Paid</p>
              <p className="text-sm font-semibold text-amber-400">{money(tc.total_already_paid)}</p>
            </div>
            <div className={`p-3 rounded-lg ${isRefund ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-red-500/10 border border-red-500/20'}`}>
              <p className="text-[10px] text-slate-500 uppercase mb-0.5">{isRefund ? 'Refund Due' : 'Amount Owed'}</p>
              <p className={`text-lg font-bold ${isRefund ? 'text-emerald-400' : 'text-red-400'}`}>{money(tc.refund_due)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Monthly Payslip Detail */}
      {data?.monthly_payslip_detail?.length > 0 && (
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-white text-sm flex items-center gap-2">
              <FileText className="h-4 w-4 text-amber-400" />
              Monthly Payslip Data
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 pt-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="payslip-table">
                <thead>
                  <tr className="text-xs text-slate-500 uppercase border-b border-slate-700">
                    <th className="text-left py-2 px-2">Month</th>
                    <th className="text-right py-2 px-2">Gross</th>
                    <th className="text-right py-2 px-2">Tax</th>
                    <th className="text-right py-2 px-2">NI</th>
                    <th className="text-right py-2 px-2">Pension</th>
                    <th className="text-right py-2 px-2">Source</th>
                  </tr>
                </thead>
                <tbody>
                  {data.monthly_payslip_detail.map(m => (
                    <tr key={m.month} className="border-b border-slate-700/50">
                      <td className="py-2 px-2 text-white">{m.month}</td>
                      <td className="py-2 px-2 text-right text-white">{money(m.gross_pay)}</td>
                      <td className="py-2 px-2 text-right text-red-400">{money(m.income_tax)}</td>
                      <td className="py-2 px-2 text-right text-red-400">{money(m.ni)}</td>
                      <td className="py-2 px-2 text-right text-orange-400">{money(m.pension)}</td>
                      <td className="py-2 px-2 text-right">
                        <Badge className={`text-[10px] ${m.source === 'manual' ? 'bg-sky-500/20 text-sky-400' : 'bg-slate-500/20 text-slate-400'}`}>
                          {m.source === 'manual' ? 'Manual' : 'Extracted'}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Earnings Reconciliation */}
      {data?.trip_breakdown?.length > 0 && data?.monthly_payslip_detail?.length > 0 && (
        <Card className="bg-slate-800 border-slate-700" data-testid="reconciliation-card">
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-white text-sm flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-purple-400" />
              Earnings Reconciliation
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 pt-0">
            <p className="text-xs text-slate-500 mb-3">Comparing trip-logged earnings against payslip data per month. Discrepancies may indicate missing trips or payslip entries.</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="reconciliation-table">
                <thead>
                  <tr className="text-xs text-slate-500 uppercase border-b border-slate-700">
                    <th className="text-left py-2 px-2">Month</th>
                    <th className="text-right py-2 px-2">Trip Earnings</th>
                    <th className="text-right py-2 px-2">Payslip Gross</th>
                    <th className="text-right py-2 px-2">Difference</th>
                    <th className="text-right py-2 px-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const tripsByMonth = {};
                    (data.trip_breakdown || []).forEach(t => {
                      const d = t.date_left || '';
                      const mk = d.substring(0, 7);
                      if (mk) tripsByMonth[mk] = (tripsByMonth[mk] || 0) + (t.earnings || 0);
                    });
                    const payslipByMonth = {};
                    (data.monthly_payslip_detail || []).forEach(p => {
                      payslipByMonth[p.month] = p.gross_pay || 0;
                    });
                    const allMonths = [...new Set([...Object.keys(tripsByMonth), ...Object.keys(payslipByMonth)])].sort();
                    return allMonths.map(mk => {
                      const tripVal = tripsByMonth[mk] || 0;
                      const payslipVal = payslipByMonth[mk] || 0;
                      const diff = tripVal - payslipVal;
                      const threshold = Math.max(tripVal, payslipVal) * 0.05;
                      const isMatch = Math.abs(diff) <= Math.max(threshold, 1);
                      const hasData = tripVal > 0 || payslipVal > 0;
                      if (!hasData) return null;
                      return (
                        <tr key={mk} className="border-b border-slate-700/50">
                          <td className="py-2 px-2 text-white">{mk}</td>
                          <td className="py-2 px-2 text-right text-white">{money(tripVal)}</td>
                          <td className="py-2 px-2 text-right text-white">{money(payslipVal)}</td>
                          <td className={`py-2 px-2 text-right font-medium ${isMatch ? 'text-emerald-400' : 'text-amber-400'}`}>
                            {diff > 0 ? '+' : ''}{money(diff)}
                          </td>
                          <td className="py-2 px-2 text-right">
                            {isMatch ? (
                              <Badge className="bg-emerald-500/20 text-emerald-400 text-[10px]">Match</Badge>
                            ) : tripVal > 0 && payslipVal === 0 ? (
                              <Badge className="bg-red-500/20 text-red-400 text-[10px]">No Payslip</Badge>
                            ) : tripVal === 0 && payslipVal > 0 ? (
                              <Badge className="bg-amber-500/20 text-amber-400 text-[10px]">No Trip</Badge>
                            ) : (
                              <Badge className="bg-amber-500/20 text-amber-400 text-[10px]">Mismatch</Badge>
                            )}
                          </td>
                        </tr>
                      );
                    });
                  })()}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Trip Breakdown */}
      {data?.trip_breakdown?.length > 0 && (
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-white text-sm flex items-center gap-2">
              <Ship className="h-4 w-4 text-sky-400" />
              Trip Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 pt-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="trip-table">
                <thead>
                  <tr className="text-xs text-slate-500 uppercase border-b border-slate-700">
                    <th className="text-left py-2 px-2">Vessel / Trip</th>
                    <th className="text-left py-2 px-2">Period</th>
                    <th className="text-right py-2 px-2">Earnings</th>
                    <th className="text-right py-2 px-2">Type</th>
                    <th className="text-right py-2 px-2">Qual. Days</th>
                  </tr>
                </thead>
                <tbody>
                  {data.trip_breakdown.map(t => (
                    <tr key={t.trip_id} className="border-b border-slate-700/50">
                      <td className="py-2 px-2 text-white">{t.vessel_name}</td>
                      <td className="py-2 px-2 text-slate-400 text-xs">{t.date_left} to {t.date_returned}</td>
                      <td className="py-2 px-2 text-right text-white">{money(t.earnings)}</td>
                      <td className="py-2 px-2 text-right">
                        <Badge className={`text-[10px] ${t.pay_type === 'PAYE' ? 'bg-blue-500/20 text-blue-400' : 'bg-amber-500/20 text-amber-400'}`}>
                          {t.pay_type}
                        </Badge>
                      </td>
                      <td className="py-2 px-2 text-right text-slate-300">{t.qualifying_days}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AccountantTaxWorkspace;
